/**
 * Watchlist Quote Store
 *
 * Holds real-time quote data for watchlist symbols outside React lifecycle.
 * Survives component unmount/remount (layout reorganization, tab switches).
 *
 * Design:
 * - _rowData (plain Map) is mutated directly by OnQuote — no React overhead per tick
 * - Zustand `rows` array is updated via throttled flush (500ms) for rendering
 * - Subscriptions are managed here, decoupled from WatchlistPanel mount state
 */
import { create } from "zustand";
import marketdata from "../data/Services/marketdata.jsx";

const THROTTLE_MS = 500;

// --- Internal (non-reactive) state ---
const _rowData = new Map(); // symbol → row object (mutated by OnQuote)
const _canonicalToAliases = new Map(); // canonical → Set<alias>
const _subscribedSymbols = new Set(); // currently subscribed symbols
let _throttleTimer = null;
let _pendingUpdate = false;
let _receiverRegistered = false;
let _currentSymbolsOrder = []; // ordered symbol list for row building

// --- Receiver (registered once, lives forever) ---
const receiver = {
  OnQuote: (quote) => {
    const matchingRows = [];
    const directRow = _rowData.get(quote.S);
    if (directRow) matchingRows.push(directRow);

    // Check canonical → aliases mapping
    const aliases = _canonicalToAliases.get(quote.S);
    if (aliases) {
      for (const alias of aliases) {
        const aliasRow = _rowData.get(alias);
        if (aliasRow) matchingRows.push(aliasRow);
      }
    } else {
      // Lazy rebuild: objectSymbolTable may not have been ready at registration time
      for (const [sym] of _rowData) {
        if (sym === quote.S) continue;
        const canonical = marketdata.getCanonicalSymbol?.(sym);
        if (canonical && canonical === quote.S && canonical !== sym) {
          if (!_canonicalToAliases.has(canonical)) {
            _canonicalToAliases.set(canonical, new Set());
          }
          _canonicalToAliases.get(canonical).add(sym);
          const aliasRow = _rowData.get(sym);
          if (aliasRow) matchingRows.push(aliasRow);
        }
      }
    }

    if (matchingRows.length === 0) return;

    for (const row of matchingRows) {
      // Update last price + tick direction for flash effect
      if (quote.C != null) {
        if (row.lastPrice != null && quote.C !== row.lastPrice) {
          row.lastTick = quote.C > row.lastPrice ? "up" : "down";
          row.tickId = (row.tickId || 0) + 1;
        }
        row.lastPrice = quote.C;
      }

      // Tick timestamp
      if (quote.DT) row.time = new Date(quote.DT);

      // Daily data fields — server sends D* on every tick, worker delta-filters unchanged values
      if (quote.DO != null) row.open = quote.DO;
      if (quote.DH != null && quote.DH !== row.high) {
        row.high = quote.DH;
        row.highTickId = (row.highTickId || 0) + 1;
      }
      if (quote.DL != null && quote.DL !== row.low) {
        row.low = quote.DL;
        row.lowTickId = (row.lowTickId || 0) + 1;
      }
      // DV = authoritative daily volume from server; Q = per-trade fallback
      if (quote.DV != null) row.volume = quote.DV;
      else if (quote.Q > 0) row.volume = (row.volume || 0) + quote.Q;
      if (quote.PC != null) row.previousClose = quote.PC;

      // Calculate change based on previous close
      if (row.lastPrice != null && row.previousClose != null) {
        row.change = row.lastPrice - row.previousClose;
        row.changePct = row.previousClose
          ? (row.change / row.previousClose) * 100
          : 0;
        row.direction = row.change > 0 ? "up" : row.change < 0 ? "down" : null;
      }

      row._dirty = true;
    }

    _scheduleFlush();
  },
};

// Last flushed row snapshots — stable references for unchanged rows
const _lastFlushedRows = new Map(); // symbol → frozen row object

function _scheduleFlush() {
  _pendingUpdate = true;
  if (_throttleTimer) return;
  _throttleTimer = setTimeout(() => {
    _throttleTimer = null;
    if (_pendingUpdate) {
      _pendingUpdate = false;
      const orderedRows = _currentSymbolsOrder
        .map((sym) => {
          const row = _rowData.get(sym);
          if (!row) return null;
          if (row._dirty) {
            row._dirty = false;
            const snapshot = { ...row };
            delete snapshot._dirty;
            _lastFlushedRows.set(sym, snapshot);
            return snapshot;
          }
          // Row unchanged — return same reference so React skips re-render
          return _lastFlushedRows.get(sym) || { ...row };
        })
        .filter(Boolean);
      useWatchlistQuoteStore.setState({ rows: orderedRows });
    }
  }, THROTTLE_MS);
}

function _ensureReceiver() {
  if (_receiverRegistered) return;
  marketdata.registerReceiver?.(receiver);
  _receiverRegistered = true;
}

function _seedRow(sym) {
  const canonical = marketdata.getCanonicalSymbol?.(sym) ?? sym;
  const quote = marketdata.getLastQuote?.(sym);

  const row = {
    symbol: sym,
    lastPrice: quote?.C ?? null,
    open: quote?.DO ?? null,
    high: quote?.DH ?? null,
    low: quote?.DL ?? null,
    volume: quote?.DV ?? 0,
    previousClose: quote?.PC ?? null,
    time: quote?.DT ? new Date(quote.DT) : null,
    change: null,
    changePct: null,
  };

  if (row.lastPrice != null && row.previousClose != null) {
    row.change = row.lastPrice - row.previousClose;
    row.changePct = row.previousClose
      ? (row.change / row.previousClose) * 100
      : 0;
    row.direction = row.change > 0 ? "up" : row.change < 0 ? "down" : null;
  }

  // Map canonical → aliases
  if (canonical !== sym) {
    if (!_canonicalToAliases.has(canonical)) {
      _canonicalToAliases.set(canonical, new Set());
    }
    _canonicalToAliases.get(canonical).add(sym);
  }

  _rowData.set(sym, row);
}

const useWatchlistQuoteStore = create(() => ({
  rows: [],
}));

/**
 * Replace watchlist subscriptions with a new symbol list.
 * Full unsubscribe/subscribe cycle — each list is the source of truth.
 * Preserves row data for symbols present in both old and new lists.
 */
export function syncSubscriptions(symbols) {
  _ensureReceiver();
  _currentSymbolsOrder = [...symbols];

  // 1. Unsubscribe ALL current symbols
  for (const sym of _subscribedSymbols) {
    marketdata.unregisterQuote?.(sym, "watchlist");
  }
  _subscribedSymbols.clear();
  _canonicalToAliases.clear();

  // 2. Build new row data — reuse existing rows, seed missing ones
  const newRowData = new Map();
  for (const sym of symbols) {
    const existing = _rowData.get(sym);
    if (existing) {
      newRowData.set(sym, existing);
    } else {
      _seedRow(sym);
      newRowData.set(sym, _rowData.get(sym));
    }
  }
  _rowData.clear();
  for (const [k, v] of newRowData) {
    _rowData.set(k, v);
  }

  // 3. Subscribe ALL new symbols
  const canonicalsToReset = new Set();
  for (const sym of symbols) {
    marketdata.registerQuote?.(sym, "watchlist");
    _subscribedSymbols.add(sym);
    canonicalsToReset.add(marketdata.getCanonicalSymbol?.(sym) ?? sym);
  }

  // 4. Reset worker's daily cache so D* fields arrive in full on the next tick.
  //    Without this, symbols that stayed subscribed via charts (refCount > 0)
  //    would have their DH/DL/PC delta-filtered by the worker.
  marketdata.resetWorkerDailyCache?.(canonicalsToReset);

  // 4. Immediate flush so UI shows current data
  const orderedRows = symbols
    .map((sym) => _rowData.get(sym))
    .filter(Boolean)
    .map((row) => ({ ...row }));
  useWatchlistQuoteStore.setState({ rows: orderedRows });
}

/**
 * Unsubscribe all symbols and clear data.
 * Called on desktop reset/load before new symbols arrive.
 */
export function clearAllSubscriptions() {
  for (const sym of _subscribedSymbols) {
    marketdata.unregisterQuote?.(sym, "watchlist");
  }
  _subscribedSymbols.clear();
  _rowData.clear();
  _lastFlushedRows.clear();
  _canonicalToAliases.clear();
  _currentSymbolsOrder = [];
  if (_throttleTimer) {
    clearTimeout(_throttleTimer);
    _throttleTimer = null;
  }
  _pendingUpdate = false;
  useWatchlistQuoteStore.setState({ rows: [] });
}

export default useWatchlistQuoteStore;
